import { useState, useEffect } from 'react';
import { CURRENT_AWARD_YEAR } from 'constants/evaluation';
import { getFormSchema, transformFormDataForStrapi } from 'forms';
import { processAIJudgmentForSubmission } from 'services/vistaJudging';
import { getRubricForCategory } from 'services/aiGuidance';
import { submitAwardApplication } from 'services/submissionService';
import { trackEvent } from 'services/firebase';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const useSubmissionForm = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [rubricData, setRubricData] = useState<any>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const smddSchema = getFormSchema('smdd', CURRENT_AWARD_YEAR) as any;
  const categories = smddSchema.categories;

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setSubmitted(false);
    setError(null);
  };

  useEffect(() => {
    const fetchRubricData = async () => {
      if (selectedCategory) {
        try {
          const rubric = await getRubricForCategory(selectedCategory);
          setRubricData(rubric);
        } catch {
          setRubricData(null);
        }
      } else {
        setRubricData(null);
      }
    };

    fetchRubricData();
  }, [selectedCategory]);

  const handleFormSubmit = async formData => {
    setIsSubmitting(true);
    setError(null);

    try {
      const transformedData = transformFormDataForStrapi(
        formData,
        selectedCategory,
        CURRENT_AWARD_YEAR
      );
      const cleanedData = { ...transformedData };

      if (
        cleanedData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedData.email)
      ) {
        throw new Error(`Invalid email format: ${cleanedData.email}`);
      }

      const requiredFields = [
        'submission_type',
        'pillar',
        'org_unit',
        'full_name',
        'title',
        'email',
        'category',
      ];
      const missingFields = requiredFields.filter(
        field => !cleanedData[field] || cleanedData[field].trim() === ''
      );
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const textFields = [
        'description',
        'technology_used',
        'innovation_description',
        'impact_and_results',
      ];
      textFields.forEach(field => {
        if (cleanedData[field] && cleanedData[field].length > 5000) {
          cleanedData[field] = cleanedData[field].substring(0, 5000) + '...';
        }
      });

      Object.keys(cleanedData).forEach(key => {
        if (typeof cleanedData[key] === 'string') {
          cleanedData[key] = cleanedData[key].trim();
        }
      });

      let recaptchaToken: string;
      if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
        recaptchaToken = 'dev-bypass';
      } else {
        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA not ready');
        }
        recaptchaToken = await executeRecaptcha('submit_award');
      }
      cleanedData.recaptchaToken = recaptchaToken;

      const result = await submitAwardApplication(cleanedData);

      if (result.data && result.data.documentId) {
        try {
          let aiRecaptchaToken: string;
          if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
            aiRecaptchaToken = 'dev-bypass';
          } else {
            if (!executeRecaptcha) {
              throw new Error('reCAPTCHA not ready');
            }
            aiRecaptchaToken = await executeRecaptcha('submit_award_ai');
          }
          await processAIJudgmentForSubmission(
            result.data.documentId,
            {},
            aiRecaptchaToken
          );
        } catch (error) {
          // Log AI judging error but don't block submission
          console.error("AI judging error but don't block submission", error);
        }
      }

      trackEvent('form_submission', {
        form_name: 'SMDD Award Submission',
        category: selectedCategory,
      });
      setSubmitted(true);
    } catch (err: any) {
      const errMsg = err.message || String(err);
      if (errMsg.includes('reach max allowed 2x submit')) {
        if (
          typeof window !== 'undefined' &&
          typeof window.alert === 'function'
        ) {
          window.alert(errMsg);
        } else {
          console.warn(errMsg);
        }
      } else {
        setError(errMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedFields = selectedCategory
    ? smddSchema.fieldsByCategory[selectedCategory]
    : null;
  const dynamicFormSchema = selectedFields ? { fields: selectedFields } : null;

  const getProgressStep = () => {
    const step = !selectedCategory ? 1 : selectedCategory && !submitted ? 2 : 3;
    return step;
  };

  return {
    selectedCategory,
    submitted,
    error,
    isSubmitting,
    rubricData,
    categories,
    handleCategoryChange,
    handleFormSubmit,
    dynamicFormSchema,
    progressStep: getProgressStep(),
  };
};
